import { useEffect } from "react";
import { Download, ExternalLink, X, FileX } from "lucide-react";
import { filesApi } from "@/lib/api";

const MAX_PREVIEW_BYTES = 25 * 1024 * 1024; // skip auto-embed for files > 25 MB

function previewKind(mime, name) {
  const m = (mime || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  if (m.startsWith("text/") || n.match(/\.(txt|csv|md|json|log)$/)) return "text";
  return "unsupported";
}

export default function FilePreviewModal({ open, onOpenChange, projectId, file, canDownload = true }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onOpenChange]);

  if (!open || !file) return null;

  const previewUrl = filesApi.previewUrl(projectId, file.id);
  const downloadUrl = filesApi.downloadUrl(projectId, file.id);
  const kind = previewKind(file.mime, file.name);
  const tooLarge = file.size > MAX_PREVIEW_BYTES;

  return (
    <div
      data-testid="file-preview-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8"
      onClick={() => onOpenChange(false)}
    >
      {/* Modal panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[90vh] flex flex-col bg-zinc-950 border border-zinc-800 rounded-md shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-zinc-800 shrink-0">
          <div className="min-w-0 flex-1">
            <div
              data-testid="preview-filename"
              className="text-sm font-medium text-zinc-100 truncate"
              title={file.name}
            >
              {file.name}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono">
              {file.mime || "—"} · {formatBytes(file.size)}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-open-new-tab"
              title="Open in new tab"
              className="p-2 rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            {canDownload && (
              <a
                href={downloadUrl}
                download={file.name}
                data-testid="preview-download-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
              >
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <button
              onClick={() => onOpenChange(false)}
              data-testid="preview-close-btn"
              title="Close (Esc)"
              className="p-2 rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto bg-black/40">
          {tooLarge ? (
            <Fallback
              icon={<FileX className="w-10 h-10 text-zinc-700" />}
              title="File is too large to preview"
              detail={
                canDownload
                  ? `${formatBytes(file.size)} — files over 25 MB are skipped to save bandwidth.`
                  : `${formatBytes(file.size)} — too large to preview, and your role doesn't allow downloads. Contact your admin for a copy.`
              }
              downloadUrl={downloadUrl}
              filename={file.name}
              canDownload={canDownload}
            />
          ) : kind === "image" ? (
            <div className="flex items-center justify-center p-4 min-h-[60vh]">
              <img
                src={previewUrl}
                alt={file.name}
                data-testid="preview-image"
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
          ) : kind === "video" ? (
            <div className="flex items-center justify-center p-4 min-h-[60vh]">
              <video
                src={previewUrl}
                controls
                autoPlay
                data-testid="preview-video"
                className="max-w-full max-h-[75vh]"
              />
            </div>
          ) : kind === "audio" ? (
            <div className="flex items-center justify-center p-8 min-h-[40vh]">
              <audio
                src={previewUrl}
                controls
                autoPlay
                data-testid="preview-audio"
                className="w-full max-w-md"
              />
            </div>
          ) : kind === "pdf" ? (
            <iframe
              src={previewUrl}
              title={file.name}
              data-testid="preview-pdf-frame"
              className="w-full h-[75vh] bg-zinc-100"
            />
          ) : kind === "text" ? (
            <iframe
              src={previewUrl}
              title={file.name}
              data-testid="preview-text-frame"
              className="w-full h-[75vh] bg-zinc-100"
            />
          ) : (
            <Fallback
              icon={<FileX className="w-10 h-10 text-zinc-700" />}
              title="Preview not available"
              detail={
                canDownload
                  ? `We can't render ${file.mime || "this file type"} in the browser. Download to view.`
                  : `We can't render ${file.mime || "this file type"} in the browser, and your role doesn't allow downloads. Contact your admin for a copy.`
              }
              downloadUrl={downloadUrl}
              filename={file.name}
              canDownload={canDownload}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Fallback({ icon, title, detail, downloadUrl, filename, canDownload = true }) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-12 min-h-[50vh]">
      <div className="mb-4">{icon}</div>
      <h3 className="font-heading text-xl text-zinc-100 mb-2">{title}</h3>
      <p className="text-zinc-500 text-sm mb-6 max-w-md">{detail}</p>
      {canDownload && (
        <a
          href={downloadUrl}
          download={filename}
          data-testid="preview-fallback-download"
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
        >
          <Download className="w-4 h-4" /> Download {filename}
        </a>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
